"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
/**
 * @author yuqzho@microsoft.com, Ning Tang
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTemplateInfosFromApiSpec = getTemplateInfosFromApiSpec;
exports.generateFilesFromApiSpec = generateFilesFromApiSpec;
const tslib_1 = require("tslib");
const m365_spec_parser_1 = require("@microsoft/m365-spec-parser");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
const lodash_1 = require("lodash");
const neverthrow_1 = require("neverthrow");
const path_1 = tslib_1.__importDefault(require("path"));
const stringUtils_1 = require("../../../common/stringUtils");
const utils_1 = require("../../../common/utils");
const question_1 = require("../../../question");
const constants_1 = require("../../../question/constants");
const CopilotGptManifestUtils_1 = require("../../driver/teamsApp/utils/CopilotGptManifestUtils");
const ManifestUtils_1 = require("../../driver/teamsApp/utils/ManifestUtils");
const generator_1 = require("../generator");
const const_1 = require("./const");
const helper_1 = require("./helper");
function normalizePath(path) {
    return "./" + path.replace(/\\/g, "/");
}
async function handleWarnings(context, inputs, warnings, teamsManifest, destinationPath, openapiSpecPath, pluginManifestPath) {
    // log warnings
    if (inputs.platform === teamsfx_api_1.Platform.CLI || inputs.platform === teamsfx_api_1.Platform.VS) {
        const warnSummary = await (0, helper_1.generateScaffoldingSummary)(warnings, teamsManifest, path_1.default.relative(destinationPath, openapiSpecPath), pluginManifestPath === undefined
            ? undefined
            : path_1.default.relative(destinationPath, pluginManifestPath), destinationPath);
        if (warnSummary) {
            context.logProvider.info(warnSummary);
        }
    }
    if (inputs.platform === teamsfx_api_1.Platform.VSCode) {
        return (0, neverthrow_1.ok)({
            warnings: warnings.map((warning) => {
                return {
                    type: warning.type,
                    content: warning.content,
                    data: warning.data,
                };
            }),
        });
    }
    else {
        return (0, neverthrow_1.ok)({ warnings: undefined });
    }
}
async function getTemplateInfosFromApiSpec(context, inputs, projectType, actionContext) {
    const templateName = inputs[question_1.QuestionNames.TemplateName];
    (0, lodash_1.merge)(actionContext?.telemetryProps, {
        ["template-name" /* telemetryProperties.templateName */]: templateName,
        ["is-declarative-copilot" /* telemetryProperties.isDeclarativeAgent */]: projectType === m365_spec_parser_1.ProjectType.Copilot ? "true" : "false",
    });
    let language = inputs[question_1.QuestionNames.ProgrammingLanguage];
    if (projectType !== m365_spec_parser_1.ProjectType.TeamsAi) {
        language =
            language === constants_1.ProgrammingLanguage.CSharp
                ? constants_1.ProgrammingLanguage.CSharp
                : constants_1.ProgrammingLanguage.None;
    }
    const safeProjectNameFromVS = language === constants_1.ProgrammingLanguage.CSharp ? inputs[question_1.QuestionNames.SafeProjectName] : undefined;
    const solutionNameFromVS = language === constants_1.ProgrammingLanguage.CSharp ? inputs[question_1.QuestionNames.SolutionName] : undefined;
    const url = inputs[question_1.QuestionNames.ApiSpecLocation].trim();
    const isYaml = !(await (0, utils_1.isJsonSpecFile)(url));
    const openapiSpecFileName = isYaml ? teamsfx_api_1.DefaultApiSpecYamlFileName : teamsfx_api_1.DefaultApiSpecJsonFileName;
    const llmServiceData = {
        llmService: inputs[question_1.QuestionNames.LLMService],
        openAIKey: inputs[question_1.QuestionNames.OpenAIKey],
        azureOpenAIKey: inputs[question_1.QuestionNames.AzureOpenAIKey],
        azureOpenAIEndpoint: inputs[question_1.QuestionNames.AzureOpenAIEndpoint],
        azureOpenAIDeploymentName: inputs[question_1.QuestionNames.AzureOpenAIDeploymentName],
    };
    const openapiSpecPath = normalizePath(path_1.default.join(teamsfx_api_1.AppPackageFolderName, teamsfx_api_1.DefaultApiSpecFolderName, openapiSpecFileName));
    const authData = inputs.apiAuthData;
    const convertedAuthData = [];
    if (authData && authData.length > 0) {
        for (const auth of authData) {
            const envName = auth.registrationId ?? (0, helper_1.getEnvName)(auth.authName ?? "");
            convertedAuthData.push({
                authName: auth.authName ?? "",
                openapiSpecPath: openapiSpecPath,
                registrationIdEnvName: envName,
                authType: auth.authType,
            });
        }
    }
    context.templateVariables = generator_1.Generator.getDefaultVariables(inputs[question_1.QuestionNames.AppName], safeProjectNameFromVS, solutionNameFromVS, inputs.targetFramework, inputs.placeProjectFileInSolutionDir === "true", convertedAuthData ?? [], llmServiceData);
    context.telemetryReporter.sendTelemetryEvent(const_1.declarativeAgentExistingApiSpecUrlTelemetryEvent, {
        ["remote-url" /* telemetryProperties.isRemoteUrlTelemetryProperty */]: (0, stringUtils_1.isValidHttpUrl)(url).toString(),
        ["generate-type" /* telemetryProperties.generateType */]: projectType.toString(),
        ["auth-type" /* telemetryProperties.authType */]: authData?.map((item) => item.authType).join(",") ?? "None",
    });
    inputs.templateState = {
        isYaml: isYaml,
        templateName: templateName,
        url: url,
        isPlugin: projectType === m365_spec_parser_1.ProjectType.Copilot,
        type: projectType,
    };
    return (0, neverthrow_1.ok)([
        {
            templateName: templateName,
            language: language,
            replaceMap: {
                ...context.templateVariables,
                DeclarativeCopilot: projectType === m365_spec_parser_1.ProjectType.Copilot ? "true" : "",
            },
        },
    ]);
}
async function generateFilesFromApiSpec(context, inputs, destinationPath, projectType, componentName) {
    const templateState = inputs.templateState;
    const isDeclarativeAgent = projectType === m365_spec_parser_1.ProjectType.Copilot;
    const manifestPath = path_1.default.join(destinationPath, teamsfx_api_1.AppPackageFolderName, teamsfx_api_1.ManifestTemplateFileName);
    const apiSpecFolderPath = path_1.default.join(destinationPath, teamsfx_api_1.AppPackageFolderName, teamsfx_api_1.DefaultApiSpecFolderName);
    const openapiSpecFileName = templateState.isYaml
        ? teamsfx_api_1.DefaultApiSpecYamlFileName
        : teamsfx_api_1.DefaultApiSpecJsonFileName;
    let openapiSpecPath = path_1.default.join(apiSpecFolderPath, openapiSpecFileName);
    if (projectType === m365_spec_parser_1.ProjectType.TeamsAi) {
        const language = inputs[question_1.QuestionNames.ProgrammingLanguage];
        if (language === constants_1.ProgrammingLanguage.CSharp) {
            openapiSpecPath = path_1.default.join(destinationPath, teamsfx_api_1.DefaultApiSpecFolderName, openapiSpecFileName);
        }
    }
    await fs_extra_1.default.ensureDir(apiSpecFolderPath);
    const pluginManifestPath = templateState.type === m365_spec_parser_1.ProjectType.Copilot
        ? path_1.default.join(destinationPath, teamsfx_api_1.AppPackageFolderName, teamsfx_api_1.DefaultPluginManifestFileName)
        : undefined;
    const responseTemplateFolder = templateState.type === m365_spec_parser_1.ProjectType.SME
        ? path_1.default.join(destinationPath, teamsfx_api_1.AppPackageFolderName, teamsfx_api_1.ResponseTemplatesFolderName)
        : undefined;
    const specParser = new m365_spec_parser_1.SpecParser(templateState.url, (0, helper_1.getParserOptions)(templateState.type, isDeclarativeAgent));
    const generateResult = await (0, helper_1.generateFromApiSpec)(specParser, manifestPath, inputs, context, componentName, templateState.type, {
        destinationApiSpecFilePath: openapiSpecPath,
        pluginManifestFilePath: pluginManifestPath,
        responseTemplateFolder,
    }, templateState.url);
    let warnings;
    if (generateResult.isErr()) {
        return (0, neverthrow_1.err)(generateResult.error);
    }
    else {
        warnings = generateResult.value.warnings;
    }
    if (isDeclarativeAgent) {
        const addActionResult = await CopilotGptManifestUtils_1.copilotGptManifestUtils.updateDeclarativeAgentManifest(manifestPath, const_1.defaultDeclarativeAgentManifestFileName, const_1.defaultDeclarativeAgentActionId, pluginManifestPath || "");
        if (addActionResult.isErr()) {
            return (0, neverthrow_1.err)(addActionResult.error);
        }
    }
    if (projectType === m365_spec_parser_1.ProjectType.TeamsAi) {
        const specs = await specParser.getFilteredSpecs(inputs[question_1.QuestionNames.ApiOperation]);
        const spec = specs[1];
        try {
            const language = inputs[question_1.QuestionNames.ProgrammingLanguage];
            const updateWarnings = await (0, helper_1.updateForCustomApi)(spec, language, destinationPath, openapiSpecFileName);
            warnings.push(...updateWarnings);
        }
        catch (error) {
            throw new teamsfx_api_1.SystemError(componentName, const_1.failedToUpdateCustomApiTemplateErrorName, error.message, error.message);
        }
    }
    const manifestRes = await ManifestUtils_1.manifestUtils._readAppManifest(manifestPath);
    if (manifestRes.isErr()) {
        return (0, neverthrow_1.err)(manifestRes.error);
    }
    const teamsManifest = manifestRes.value;
    return handleWarnings(context, inputs, warnings, teamsManifest, destinationPath, openapiSpecPath, pluginManifestPath);
}
//# sourceMappingURL=common.js.map